import {
    getParentCategoryFromSubCategory,
    isSubCategory,
} from '@eventbrite/categories';
import { useQuery } from '@tanstack/react-query';
import shuffle from 'lodash/shuffle';
import {
    OrganicEventSearchApiResponse,
    OrganicEventSearchFiltersApiPayload,
    OrganizerAutocompleteAggApiResponse,
    TagAggApiResponse,
} from '../../../../api/searchOrganicEvents';
import { search } from '../../api';
import { useSearchContext } from '../../context';
import { useBaseEventsQuery } from './events';
import { useLocation } from './location';

const normalizeTags = (data: OrganicEventSearchApiResponse) => {
    return data.events.aggs?.tags?.buckets.reduce<any>(
        (memo: TagAggApiResponse, bucket: TagAggApiResponse) => ({
            ...memo,
            [bucket.key]: bucket,
        }),
        {},
    );
};

export const useBaseEventsQueryTagsAgg = () =>
    useBaseEventsQuery(normalizeTags, {}, { optOutDedupeEvents: true });

export const useCurrentBaseTag = () => {
    const { query } = useSearchContext();
    const tag = query?.tags?.[0] || '';

    const { data } = useBaseEventsQueryTagsAgg();

    if (tag && data) {
        return data[tag];
    }
};

//Remove duplicates like "boat_party" and "boatparty"
const badDedupeOrganizerTags = (
    tags: OrganizerAutocompleteAggApiResponse[],
) => {
    const seen = new Set();
    const dedupedTags = tags.reduce<OrganizerAutocompleteAggApiResponse[]>(
        (acc, tag) => {
            let bigram = tag.display_name;
            let words;

            if (tag.display_name.includes('_')) {
                words = tag.display_name.split('_');
                bigram = words.join('');
            }

            if (seen.has(bigram)) {
                return acc;
            }

            if (words) {
                if (words.find((word: string) => seen.has(word))) return acc;
            }

            seen.add(bigram);
            return [...acc, tag];
        },
        [],
    );

    return dedupedTags;
};

const getRandomOrganizerTags = (data: OrganicEventSearchApiResponse) => {
    const organizerTagsArray =
        data.events.aggs?.organizertagsautocomplete_agg?.buckets;

    const shuffledTags = shuffle(organizerTagsArray);
    const dedupedTags = badDedupeOrganizerTags(shuffledTags);

    return dedupedTags
        .slice(0, 25)
        .map((agg) => ({ ...agg, count: agg.doc_count }));
};

export const useRandomOrganizerTags = () =>
    useBaseEventsQuery(
        getRandomOrganizerTags,
        {},
        { optOutDedupeEvents: true },
    );

/**
 * Selector leveraged alongside useCategorySubCategories
 *
 * Filters array of tags to return only ones that should
 * be rendered.
 *
 * @param data
 * @returns TagAggResponse[]
 */
const normalizeAndFilterSubcategories = (
    data: OrganicEventSearchApiResponse,
) => {
    const tags = data.events.aggs?.tags?.buckets || [];
    const subCategories = tags.filter(
        (tag: TagAggApiResponse) =>
            tag.key.includes('EventbriteSubCategory') &&
            (tag?.doc_count || 0) >= 3 &&
            !tag.display_name.includes('other') &&
            !tag.display_name.includes('Other'),
    );

    return subCategories;
};

/**
 * Hook that fetches subcategories to render.
 *
 * Depending on current active tag (parent or sub)
 * it will properly fetch the appropriate response
 * to render subcategories appropriately.
 *
 * @returns TaggAggResponse[] | undefined
 */
export function useCategorySubCategories() {
    const { query } = useSearchContext();
    const activeTagKey = query.tags?.[0] || '';

    const parentCategoryKey = isSubCategory(activeTagKey)
        ? getParentCategoryFromSubCategory(activeTagKey)
        : activeTagKey;

    //Query overload should match the existing category query to attempt
    //and re-leverage the cached content. When directly linking to a
    //sub category page it will trigger a preemptive response.
    const queryOverload: OrganicEventSearchFiltersApiPayload = parentCategoryKey
        ? {
              ...query,
              tags: [parentCategoryKey],
          }
        : {};

    return useBaseEventsQuery(normalizeAndFilterSubcategories, queryOverload, {
        optOutDedupeEvents: true,
    });
}

export function useLocationAggregations() {
    const { data: location } = useLocation();

    const query: OrganicEventSearchFiltersApiPayload = {
        aggs: { tags: { size: 50 } },
        dates: ['current_future'],
        places: location?.placeId ? [location?.placeId] : undefined,
        online_events_only: location?.isOnline,
        page_size: 0,
    };

    const response = useQuery(
        ['category-browse', query],
        async () => await search(query, {}),
        {
            select: (response) => response.events.aggs,
            enabled: !!location,
        },
    );

    return response;
}
